import {Award, ExternalLink, Shield, Star, TrendingUp} from 'lucide-react'
import { Link } from "react-router-dom"
import Breadcrumbs from "@/components/seo/Breadcrumbs"
import JsonLd from "@/components/seo/JsonLd"
import MedicalDisclaimer from "@/components/seo/MedicalDisclaimer"
import SeoHead, { SITE_NAME, SITE_URL } from "@/components/seo/SeoHead"
import { reviewProducts } from "@/data/reviews"

function Reviews() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Top Reviews", item: `${SITE_URL}/reviews` }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Top Vitamin Reviews",
      url: `${SITE_URL}/reviews`,
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: reviewProducts.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}/reviews/${product.slug}`,
          name: `${product.name} Review`
        }))
      }
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
      <SeoHead
        title="Top Vitamin Reviews | Vitamin Vital Portal"
        description="Expert Vitamin Vital Portal reviews of B Vital Total, D Vital 10,000, and Immunovit Plus. Compare ratings, dosage, and check the best price."
        path="/reviews"
        type="website"
      />
      <JsonLd data={schema} />

      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Award className="w-5 h-5" />
              <span className="text-sm font-medium">Expert-Reviewed Supplements</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">Top Vitamin Reviews</h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Evidence-based reviews of the best vitamin supplements for energy, immunity, and overall vitality
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">2,500+</div>
              <div className="text-sm text-gray-600">Products Analyzed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">15+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">50K+</div>
              <div className="text-sm text-gray-600">Happy Readers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-1">100%</div>
              <div className="text-sm text-gray-600">Unbiased Reviews</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Top Reviews" }]} />

        <div className="overflow-x-auto bg-white rounded-2xl shadow-lg mb-12">
          <table className="w-full">
            <thead className="bg-teal-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">Product</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Best For</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Rating</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Price</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Review</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reviewProducts.map((product) => (
                <tr key={product.slug} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">{product.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{product.category}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{product.rating} / 5</td>
                  <td className="px-6 py-4 text-sm font-semibold text-teal-700">{product.price}</td>
                  <td className="px-6 py-4">
                    <Link to={`/reviews/${product.slug}`} className="text-teal-700 font-medium hover:text-teal-800">
                      Read review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-12">
          {reviewProducts.map((product) => (
            <div key={product.slug} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-8 p-8">
                <div>
                  <div className="relative rounded-xl overflow-hidden mb-6 bg-gradient-to-br from-teal-50 to-orange-50 p-8">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <div className="absolute top-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-full font-bold text-sm">
                      Editor's Choice
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < Math.floor(product.rating)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="font-semibold text-gray-900">{product.rating}</span>
                    <span className="text-gray-500">({product.reviews} reviews)</span>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Best Price</div>
                      <div className="text-3xl font-bold text-teal-600">{product.price}</div>
                    </div>
                    <a
                      href={product.affiliateUrl}
                      className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                    >
                      Check Best Price
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  <Link
                    to={`/reviews/${product.slug}`}
                    className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
                  >
                    Read Full Review →
                  </Link>
                </div>

                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">{product.name}</h2>
                  <p className="text-lg text-gray-600 mb-6">{product.tagline}</p>

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-teal-600" />
                      Key Benefits
                    </h3>
                    <ul className="space-y-2">
                      {product.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2 flex-shrink-0" />
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-teal-600" />
                      Quality Features
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {product.keyFeatures.map((feature) => (
                        <div key={feature} className="bg-teal-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-br from-teal-50 to-orange-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Our Review Methodology</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Quality Testing</h3>
              <p className="text-sm text-gray-600">
                We verify third-party testing, ingredient purity, and manufacturing standards for every product.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Clinical Evidence</h3>
              <p className="text-sm text-gray-600">
                Each recommendation is backed by peer-reviewed research and clinical studies on ingredient efficacy.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Real User Feedback</h3>
              <p className="text-sm text-gray-600">
                We analyze thousands of verified customer reviews to assess real-world effectiveness and side effects.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <MedicalDisclaimer extra="Vitamin Vital Portal may earn affiliate commissions from purchases made through links on this page." />
        </div>
      </div>
    </div>
  )
}

export default Reviews
